import { createClient } from '../../../lib/apollo-client';
import {
  HelpLayout,
  ExitPreview,
  PortableTextWrapper,
  createFaqCategoryLinks,
  createFaqBreadcrumbs,
  Breadcrumbs,
  getAllFaq,
  getFaqPageData,
  getAllSettings,
  getFaqCategoryBySlug,
} from '@web-creditea/utils';
import { Typography, Gutter, useIsScreenSize, LinkAsButton } from '@ipf-ui/components-library';
import {
  FAQ_TRANSLATION,
  MARKET,
  PRIMARY_LANGUAGE_CODE,
  PRIMARY_FAQ_BREADCRUMB_TRANSLATION,
  SECONDARY_FAQ_BREADCRUMB_TRANSLATION,
  REVALIDATION_TIME,
} from '../../../src/constants';
import {
  GetSingleFaqDocument,
  GetSingleFaqQuery,
  GetSingleFaqQueryVariables,
  TextRaw,
} from '@web-creditea/interfaces';
import { GetStaticPaths, InferGetStaticPropsType } from 'next';
import Link from 'next/link';

const Helps = ({
  breadcrumbs,
  heroSection,
  helpCategories,
  language,
  postData,
  preview,
  seo,
  translations,
  additionalSections,
  settings,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { isScreenXS } = useIsScreenSize();
  const constants = {
    language,
    market: MARKET,
    translations: {
      ...translations,
      faqTranslation: FAQ_TRANSLATION,
    },
  };

  return (
    <HelpLayout
      helpCategories={helpCategories}
      settings={settings}
      seo={seo}
      heroSection={heroSection}
      additionalSections={additionalSections}
      constants={constants}
      showSideMenu={true}
    >
      {preview ? <ExitPreview /> : null}
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Gutter size={60} />
      <Typography.Header3>{postData.title}</Typography.Header3>
      {!isScreenXS ? <Gutter size={32} /> : <Gutter size={24} />}
      <PortableTextWrapper portableText={postData.content.textRaw} />
      {postData.faqButton ? <Gutter size={60} /> : <Gutter size={100} />}
      {postData.faqButton && (
        <>
          <LinkAsButton
            NextLink={Link}
            linkData={postData.faqButton}
            mode={'dark'}
            variant={'primary'}
            small
          />
          <Gutter size={48} />
        </>
      )}
    </HelpLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const localesUpperCase = locales.map((locale) => locale.toUpperCase());
  const client = createClient();
  const allFaq = await getAllFaq(client)(localesUpperCase, MARKET);

  const paths = allFaq.reduce((acc, item) => {
    if (item.faqCategory) {
      acc.push({
        params: {
          id: item.slug.current,
          category: item.faqCategory?.category?.slug?.current,
        },
        locale: item.language.toLowerCase(),
      });
    }
    return acc;
  }, []);

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ params, locale, preview }) => {
  const language = String(locale).toUpperCase();
  const client = createClient();

  // Single Post data
  const {
    data: { allFaq: allFaqQuestions },
  } = await client.query<GetSingleFaqQuery, GetSingleFaqQueryVariables>({
    query: GetSingleFaqDocument,
    variables: { language, market: MARKET, slug: params.id },
    fetchPolicy: 'no-cache',
  });

  const postDraft = allFaqQuestions.find((faq: { _id: string }) => faq._id?.startsWith('drafts'));
  const publishedPost = allFaqQuestions.find(
    (faq: { _id: string }) => !faq._id?.startsWith('drafts')
  );

  const post = preview && postDraft ? postDraft : publishedPost;

  const faqCategory = await getFaqCategoryBySlug(client)(
    language,
    MARKET,
    params.category,
    preview
  );
  const postSlug = post?.slug?.current;

  if (!faqCategory?.category || !postSlug) {
    return {
      notFound: true,
    };
  }

  const postData = {
    id: postSlug,
    category: faqCategory.category,
    title: post.title,
    content: post.content as { textRaw: TextRaw },
    faqButton: post.faqButton,
  };

  // faqPageData
  const pageData = await getFaqPageData(client)(language, MARKET, preview);
  if (!pageData) {
    return { notFound: true };
  }
  const { Hero, linkSection, translations, additionalSections } = pageData;

  const helpCategories = createFaqCategoryLinks(linkSection.items, FAQ_TRANSLATION);

  const breadcrumbFaqTranslation =
    language === PRIMARY_LANGUAGE_CODE.toUpperCase()
      ? PRIMARY_FAQ_BREADCRUMB_TRANSLATION
      : SECONDARY_FAQ_BREADCRUMB_TRANSLATION;
  const breadcrumbs = createFaqBreadcrumbs(
    breadcrumbFaqTranslation,
    post.faqCategory.category,
    post.title,
    PRIMARY_FAQ_BREADCRUMB_TRANSLATION
  );

  const settings = await getAllSettings(client, MARKET, language, preview);

  return {
    props: {
      locale,
      breadcrumbs,
      helpCategories,
      postData,
      preview: Boolean(preview),
      seo: post.seoGroup,
      heroSection: Hero,
      additionalSections,
      language,
      translations,
      settings,
    },
    revalidate: REVALIDATION_TIME,
  };
};

export default Helps;
